export interface Timeframe
{
  name: string;
  start: Date;
  end: Date;
  type: TimeframeType;
  selected: boolean;
}

export enum TimeframeType {
    day = 1, week = 2, month = 3, year = 4, allTime = 5, custom = 6
} 

function formatDate(date: Date): string {
    return date.toLocaleDateString(undefined);
}

function formatRange(start: Date, end: Date): string {
    return `${formatDate(start)} to ${formatDate(end)}`;
}

function formatMonthYear(date: Date): string {
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function formatYear(date: Date): string {
    return date.getFullYear().toString();
}

function formatDayName(date: Date): string {
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  return `${dayName} ${day} ${month} ${year}`;
}

export function getTimeframes(earliest: Date): Timeframe[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today.getDay(); // 0 = Sunday

    // Individual days of this week, always including today and only days from this week
    const days: Timeframe[] = [];
    const startOfThisWeek = new Date(today);
    startOfThisWeek.setHours(0, 0, 0, 0);
    startOfThisWeek.setDate(today.getDate() - today.getDay()); // Sunday of this week

    for (let i = 0; i <= today.getDay(); i++) {
      const day = new Date(startOfThisWeek);
      day.setDate(startOfThisWeek.getDate() + i);
      days.push({
        name: formatDayName(day),
        start: new Date(day),
        end: new Date(day),
        type: TimeframeType.day,
        selected: day.toDateString() === today.toDateString()
      });
    }

  // This week (Sunday to Saturday)
    startOfThisWeek.setDate(today.getDate() - dayOfWeek);
    const endOfThisWeek = new Date(today);
    endOfThisWeek.setDate(today.getDate() + (6 - today.getDay()));
    const thisWeek: Timeframe = {
        name: formatRange(startOfThisWeek, endOfThisWeek),
        start: startOfThisWeek,
        end: endOfThisWeek,
        type: TimeframeType.week,
        selected: true
    };

    // This month
    const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfThisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const thisMonth: Timeframe = {
        name: formatMonthYear(startOfThisMonth),
        start: startOfThisMonth,
        end: endOfThisMonth,
        type: TimeframeType.month,
        selected: false
    };

    // This year (start: Jan 1, end: Dec 31)
    const startOfThisYear = new Date(today.getFullYear(), 0, 1);
    const endOfThisYear = new Date(today.getFullYear(), 11, 31);
    const thisYear: Timeframe = {
        name: formatYear(startOfThisYear),
        start: startOfThisYear,
        end: endOfThisYear,
        type: TimeframeType.year,
        selected: false
    };

    // Only add if earliest is before or within the timeframe
    const timeframes: Timeframe[] = [
        ...days,
        thisWeek
    ];

    // Last week (previous Sunday to previous Saturday)
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfThisWeek);
    endOfLastWeek.setDate(startOfThisWeek.getDate() - 1);
    if (earliest <= endOfLastWeek) {
        timeframes.push({
            name: formatRange(startOfLastWeek, endOfLastWeek),
            start: startOfLastWeek,
            end: endOfLastWeek,
            type: TimeframeType.week,
            selected: false
        });
    }

    // Week before last
    const startOfWeekBeforeLast = new Date(startOfLastWeek);
    startOfWeekBeforeLast.setDate(startOfLastWeek.getDate() - 7);
    const endOfWeekBeforeLast = new Date(startOfLastWeek);
    endOfWeekBeforeLast.setDate(startOfLastWeek.getDate() - 1);
    if (earliest <= endOfWeekBeforeLast) {
        timeframes.push({
            name: formatRange(startOfWeekBeforeLast, endOfWeekBeforeLast),
            start: startOfWeekBeforeLast,
            end: endOfWeekBeforeLast,
            type: TimeframeType.week,
            selected: false
        });
    }

    // Always add this month
    timeframes.push(thisMonth);

    // Last month
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    if (earliest <= endOfLastMonth) {
        timeframes.push({
            name: formatMonthYear(startOfLastMonth),
            start: startOfLastMonth,
            end: endOfLastMonth,
            type: TimeframeType.month,
            selected: false
        });
    }

    // Month before last
    const startOfMonthBeforeLast = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const endOfMonthBeforeLast = new Date(today.getFullYear(), today.getMonth() - 1, 0);
    if (earliest <= endOfMonthBeforeLast) {
        timeframes.push({
            name: formatMonthYear(startOfMonthBeforeLast),
            start: startOfMonthBeforeLast,
            end: endOfMonthBeforeLast,
            type: TimeframeType.month,
            selected: false
        });
    }

    // Always add this year
    timeframes.push(thisYear);

    // Last year
    const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
    const endOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
    if (earliest <= endOfLastYear) {
        timeframes.push({
            name: formatYear(startOfLastYear),
            start: startOfLastYear,
            end: endOfLastYear,
            type: TimeframeType.year,
            selected: false
        });
    }

    // All time
    timeframes.push({
        name: 'All Time',
        start: null as any,
        end: null as any,
        type: TimeframeType.allTime,
        selected: false
    });

    return timeframes;
}
